import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

import GroupPageShell from "../components/common/GroupPageShell";
import DivisionIntro from "../components/common/DivisionIntro";
import DivisionContact from "../components/common/DivisionContact";
import DivisionClients from "../components/common/DivisionClients";
import DivisionClientsStrip from "../components/common/DivisionClientsStrip";

import { Building2, Phone, Users, Wrench } from "lucide-react";
import { buildClientLogoImages } from "../utils/clientLogos";
import DivisionServices from "../components/common/DivisionServices";

const HamdanPlaza: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => {
        setContent(data.Plaza || {});
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Default tab is "about"
  const activeTab = searchParams.get("tab") || "about";

  const menuItems = [
    { id: "about", label: t('common.about'), icon: <Building2 className="w-5 h-5" /> },
    { id: "services", label: t('common.services'), icon: <Wrench className="w-5 h-5" /> },
    { id: "clients", label: t('common.clients'), icon: <Users className="w-5 h-5" /> },
    { id: "contact", label: t('common.contact'), icon: <Phone className="w-5 h-5" /> },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;

  const clientLogoImages = buildClientLogoImages(content);
  const clientLogoUrls = t_api(content?.clients_logo_urls, content?.clients_logo_urls_ar);

  return (
    <GroupPageShell
      heroContent={{
        banners: [
          { image: content?.banner_1 || "/banner/hamdanPlaza_1.jpg" },
          { image: content?.banner_2 || "/banner/hamdanPlaza_2.jpg" },
          { image: content?.banner_3 || "/banner/hamdanPlaza_3.jpg" }
        ],
        title: t_api(content?.banner_title, content?.banner_title_ar),
        subtitle: t_api(content?.banner_subtitle, content?.banner_subtitle_ar)
      }}
      tabs={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId) => setSearchParams({ tab: tabId })}
    >
      <div className="mt-8 ">
        {activeTab === "about" && (
          <>
            <DivisionIntro
              content={{
                kicker: "Hamdan Group - Oman",
                title: t_api(content?.about_title, content?.about_title_ar),
                desc: t_api(content?.about_desc, content?.about_desc_ar),
                image: content?.about_image || "/Hamdan-Plaza-logo.jpg",
                imageFit: "contain",
                imageBadgeTitle: "Hospitality",
                imageLabel: t_api(content?.about_title, content?.about_title_ar) || "Hamdan Plaza Hotel",
                noteTitle: "Website",
                noteDesc: content?.about_website || "www.hamdanplazahotel.com",
                panelTitle: "INTRODUCTION",
                capabilitiesTitle: "Hotel Highlights",
                capabilitiesMeta: "Location - Comfort - Service",
                brandLogo: "/Hamdan-Plaza-logo.jpg",
                brandDesc: `For more information please visit our website: ${content?.about_website || 'www.hamdanplazahotel.com'}`,
                brandTags: ["Hotel", "Hospitality", "Salalah"],
                points: [1, 2, 3].map(i => ({
                  title: t_api(content?.[`about_pt_${i}_title`], content?.[`about_pt_${i}_title_ar`]),
                  desc: t_api(content?.[`about_pt_${i}_desc`], content?.[`about_pt_${i}_desc_ar`]),
                  icon: content?.[`about_pt_${i}_icon`]
                })).filter(p => p.title)
              }}
            />
            <DivisionClientsStrip
              title={t_api(content?.clients_title, content?.clients_title_ar)}
              desc={t_api(content?.clients_desc, content?.clients_desc_ar)}
              clientsText={t_api(content?.clients_list, content?.clients_list_ar)}
              logoUrls={clientLogoUrls}
              logoImages={clientLogoImages}
            />
          </>
        )}
        {activeTab === "services" && (
          <DivisionServices content={content} />
        )}
        {activeTab === "clients" && <DivisionClients content={{
          title: t_api(content?.clients_title, content?.clients_title_ar),
          desc: t_api(content?.clients_desc, content?.clients_desc_ar),
          clientsText: t_api(content?.clients_list, content?.clients_list_ar),
          logoUrls: clientLogoUrls,
          logoImages: clientLogoImages,
          divisionLabel: "Hamdan Plaza Hotel",
          footer: t_api(content?.clients_footer, content?.clients_footer_ar),
          footerDesc: t_api(content?.clients_footer_desc, content?.clients_footer_desc_ar),
        }} />}
        {activeTab === "contact" && <DivisionContact content={{
          title: t_api(content?.contact_form_title, content?.contact_form_title_ar),
          infoTitle: t_api(content?.contact_info_title, content?.contact_info_title_ar),
          infoDesc: t_api(content?.contact_info_desc, content?.contact_info_desc_ar),
          contactInfo: [{
            location: "Hamdan Plaza Hotel",
            address: t_api(content?.contact_address, content?.contact_address_ar),
            phone: content?.contact_phone,
            fax: content?.contact_fax,
            email: content?.contact_email,
          }],
          supportInfo: content?.contact_support_phone ? [{ label: "Telephone", value: content.contact_support_phone }] : [],
          supportEmail: content?.contact_email,
        }} />}
      </div>
    </GroupPageShell>
  );
};

export default HamdanPlaza;
